import { Col, Form, Input, Radio } from 'antd'
import { FormInstance } from 'antd/lib'
import { useState } from 'react'

import { Permissions } from 'src/api/requests'
import { UsersInfinitySelect } from 'src/features/activity/components'
import { useLocales } from 'src/locales'

/** There is no "doctor" flag on a profile; being assignable to a person is what
 *  marks a user as one, the same way the add-doctor flow picks them. */
const DOCTOR_PERMISSIONS = [
  Permissions.PERSONS_USER_ASSIGNMENT_USER_CAN_HAVE_PERSON_ASSIGNED
]

type ReferralDoctorSource = 'registered' | 'freeText'

export type ReferralDoctorFormValues = {
  referralDoctorUserId?: string | null
  referralDoctorName?: string | null
}

type Props = {
  form: FormInstance
  /** Starts on the free-text branch when the doctor was saved as plain text. */
  initialSource?: ReferralDoctorSource
  /** The registered doctor's display name, when known upfront. Pre-seeds the
   *  select so it shows the name instead of the raw id while the doctor isn't
   *  part of the list's first loaded page yet. */
  initialUserOption?: { value: string; label: string }
}

/** The doctor who referred (sent) the patient: either a user registered in the
 *  system or, when they are not, their name as free text. The external result
 *  and the investigation demand contracts both carry the pair, so the block is
 *  shared. Renders columns, so it belongs inside a Row. */
export const ReferralDoctorFields = ({
  form,
  initialSource = 'registered',
  initialUserOption
}: Props) => {
  const { t } = useLocales()

  const [source, setSource] = useState<ReferralDoctorSource>(initialSource)

  // Only one of the two fields may reach the backend, so switching the branch
  // clears whatever the other one held.
  const onSourceChange = (value: ReferralDoctorSource) => {
    setSource(value)
    form.resetFields(['referralDoctorUserId', 'referralDoctorName'])
  }

  return (
    <>
      <Col span={24}>
        <Form.Item label={t('investigations.referral_doctor')}>
          <Radio.Group
            value={source}
            onChange={(e) => onSourceChange(e.target.value)}
          >
            <Radio value="registered">
              {t('investigations.referral_doctor_registered')}
            </Radio>
            <Radio value="freeText">
              {t('investigations.referral_doctor_free_text')}
            </Radio>
          </Radio.Group>
        </Form.Item>
      </Col>

      <Col span={24}>
        {source === 'registered' ? (
          <Form.Item name="referralDoctorUserId">
            {/* No legalEntity filter on purpose: the referring doctor usually
                works at another institution than the one recording the test. */}
            <UsersInfinitySelect
              className="full-width"
              permissionKeys={DOCTOR_PERMISSIONS}
              placeholder={t('investigations.referral_doctor')}
              initialOptions={
                initialUserOption ? [initialUserOption] : undefined
              }
            />
          </Form.Item>
        ) : (
          <Form.Item name="referralDoctorName">
            <Input
              maxLength={200}
              placeholder={t('investigations.referral_doctor_name')}
            />
          </Form.Item>
        )}
      </Col>
    </>
  )
}

/** Only one branch is ever filled, so the payload simply keeps whichever it is. */
export const pickReferralDoctor = (values: ReferralDoctorFormValues) => ({
  referralDoctorUserId: values.referralDoctorUserId || undefined,
  referralDoctorName: values.referralDoctorName?.trim() || undefined
})
